<template>
  <div class="container" v-loading.fullscreen.lock="loading" :element-loading-text="loadingText">
    <el-button style="margin-bottom: 20px;" @click="confirmExport">生成報告</el-button>

    <el-form label-position="top" label-width="140px" style="display: flex; justify-content: center;">
      <el-row :gutter="24">
        <!-- 左側：一般輸入 -->
        <el-col :xs="24" :sm="14" :md="12">
          <el-form-item label="請輸入颱風名稱(如: 丹娜絲)">
            <el-input v-model="manual_content.typhoon_name" :placeholder="manual_content.typhoon_name" />
          </el-form-item>

          <el-form-item>
            <el-input v-model="manual_content.report_number" :placeholder="manual_content.report_number" />
          </el-form-item>

          <el-form-item>
            <el-input v-model="manual_content.sub_title" :placeholder="manual_content.sub_title" />
          </el-form-item>

          <el-form-item>
            <el-input v-model="manual_content.report_date" :placeholder="manual_content.report_date" />
          </el-form-item>
        </el-col>

        <!-- 右側：兩個時間選擇器 -->
        <el-col :xs="24" :sm="10" :md="12">
          <el-form-item label="請選擇數據排名表時間範圍(最多七天)">
            <el-date-picker 
              v-model="selectedDate" 
              type="datetimerange" 
              start-placeholder="Start Date"
              end-placeholder="End Date" 
              :clearable="false"
              format="yyyy/MM/dd HH:mm"
              :picker-options="datePickerOptions"
              :default-time="['00:00:00', '23:59:00']"
              @change="getDate"
              style="width: 100%;"
            />
          </el-form-item>

          <el-form-item label="請選擇日雨量累積圖時間點">
            <el-date-picker
              v-model="rain_time"
              type="datetime"
              format="yyyy/MM/dd HH:mm"
              :picker-options="datePickerOptions"
              placeholder="Select Date and Time"
              @change="updateRainTime"
              style="width: 100%;"
            />
          </el-form-item>
        </el-col>
      </el-row>
    </el-form>
    
    <div v-if="alertData" class="alert-box" ref="alertBox">
      <div v-if="alertData.sections.length">
        <div v-for="(section, index) in alertData.sections" :key="index">
          <p>{{ section.content }}</p>
        </div>
      </div>
    </div>

    <div class="image-container" ref="imageContainer" style="margin-bottom: 20px;">
      <img 
        v-for="(imgSrc, index) in images" 
        :key="index" 
        :src="imgSrc" 
        alt="降水量預測圖" />
    </div>
    
    <div class="image-container" ref="rainImageContainer" style="margin-bottom: 20px;" v-if="isDataLoaded">
      <img 
        v-for="(imgSrc, index) in rain_images" 
        :key="index" 
        :src="imgSrc" 
        alt="日累積雨量" />
    </div>
    
    <div v-if="isDataLoaded">
      <Map4Autoreport ref="mapComponent" :project-data="projectData" />
    </div>

    <div style="width: fit-content; margin-top: 20px;" ref="rankTable1">
      <RankTable 
        v-if="isDataLoaded"
        @top-instruments="handleTopInstruments($event, 'rank1')"
        :default-start="start"
        :default-end="end"
      />
    </div>

    <div style="width: fit-content; margin-top: 20px;" ref="rankTable2">
      <RankTable 
        v-if="isDataLoaded"
        @top-instruments="handleTopInstruments($event, 'rank2')"
        :default-start="start"
        :default-end="end"
        :defaultTMOption="'tm_rate'"
      />
    </div>

  <!-- chartBox1：累積變位 -->
  <!-- 地滑計 (mm) -->
  <div style="width: fit-content; margin-top: 20px;" ref="chartBox1_TM">
    <el-card class="box-card">
      <template v-if="chartReplaced.chartBox1_TM && chartImages.chartBox1_TM">
        <img :src="chartImages.chartBox1_TM" alt="chartBox1_TM" style="width: 100%; border: 1px solid #ccc;" />
      </template>
      
      <Chart 
        v-else-if="flat1_TM.length"
        :key="chartKey + '_1tm'"
        :useUrl="useUrl"
        :presetInstrumentIds="flat1_TM"
        :presetDateRange="[past_7days_start, end]"
        :autoRun="true"
        :autoGroup="false"
        :interactive="false"
        :y-axis-fixed-map="{ mm: { min: -30, max: 30, tickInterval: 10 } }"
        @ready="payload => handleChartReady('chartBox1_TM', payload)"
      />
    </el-card>
  </div>

  <!-- 傾斜計 (度) -->
  <div style="width: fit-content; margin-top: 20px;" ref="chartBox1_STI">
    <el-card class="box-card">
      <template v-if="chartReplaced.chartBox1_STI && chartImages.chartBox1_STI">
        <img :src="chartImages.chartBox1_STI" alt="chartBox1_STI" style="width: 100%; border: 1px solid #ccc;" />
      </template>
      
      <Chart 
        v-else-if="flat1_STI.length"
        :key="chartKey + '_1sti'"
        :useUrl="useUrl"
        :presetInstrumentIds="flat1_STI"
        :presetDateRange="[past_7days_start, end]"
        :autoRun="true"
        :autoGroup="false"
        :interactive="false"
        :y-axis-fixed-map="{ 度: { min: -1, max: 1, tickInterval: 0.25 } }"
        @ready="payload => handleChartReady('chartBox1_STI', payload)"
      />
    </el-card>
  </div>


  <!-- chartBox2：歸零曲線 -->
  <!-- 地滑計 (mm) -->
  <div style="width: fit-content; margin-top: 20px;" ref="chartBox2_TM">
    <el-card class="box-card">
      <template v-if="chartReplaced.chartBox2_TM && chartImages.chartBox2_TM">
        <img :src="chartImages.chartBox2_TM" alt="chartBox2_TM" style="width: 100%; border: 1px solid #ccc;" />
      </template>
      
      <Chart 
        v-else-if="flat2_TM.length && isChart1Loaded"
        :key="chartKey2 + '_2tm'"
        :useUrl="useUrl"
        :presetInstrumentIds="flat2_TM"
        :presetDateRange="[past_7days_start, end]"
        :autoRun="true"
        :autoGroup="false"
        :autoCompare="true"
        :interactive="false"
        :y-axis-fixed-map="{ mm: { min: -12, max: 12, tickInterval: 4 } }"
        @ready="payload => handleChartReady('chartBox2_TM', payload)"
      />
    </el-card>
  </div>

  <!-- 傾斜計 (度) -->
  <div style="width: fit-content; margin-top: 20px;" ref="chartBox2_STI">
    <el-card class="box-card">
      <template v-if="chartReplaced.chartBox2_STI && chartImages.chartBox2_STI">
        <img :src="chartImages.chartBox2_STI" alt="chartBox2_STI" style="width: 100%; border: 1px solid #ccc;" />
      </template>
      
      <Chart 
        v-else-if="flat2_STI.length && isChart1Loaded"
        :key="chartKey2 + '_2sti'"
        :useUrl="useUrl"
        :presetInstrumentIds="flat2_STI"
        :presetDateRange="[past_7days_start, end]"
        :autoRun="true"
        :autoGroup="false"
        :autoCompare="true"
        :interactive="false"
        :y-axis-fixed-map="{ 度: { min: -0.3, max: 0.3, tickInterval: 0.1 } }"
        @ready="payload => handleChartReady('chartBox2_STI', payload)"
      />
    </el-card>
  </div>


  <!-- chartBox3：1小時速率 -->
  <!-- 地滑計 -->
  <div style="width: fit-content; margin-top: 20px;" ref="chartBox3_TM">
    <el-card class="box-card">
      <template v-if="chartReplaced.chartBox3_TM && chartImages.chartBox3_TM">
        <img :src="chartImages.chartBox3_TM" alt="chartBox3_TM" style="width: 100%; border: 1px solid #ccc;" />
      </template>
      
      <Chart 
        v-else-if="flat2_TM.length && isChart2Loaded"
        :key="chartKey + '_3tm'"
        :useUrl="useUrl"
        :presetInstrumentIds="flat2_TM"
        :presetDateRange="[past_7days_start, end]"
        :autoRun="true"
        :autoGroup="false"
        :interactive="false"
        :autoVelocity1Hr="true"
        @ready="payload => handleChartReady('chartBox3_TM', payload)"
      />
    </el-card>
  </div>

  <!-- 傾斜計 -->
  <div style="width: fit-content; margin-top: 20px;" ref="chartBox3_STI">
    <el-card class="box-card">
      <template v-if="chartReplaced.chartBox3_STI && chartImages.chartBox3_STI">
        <img :src="chartImages.chartBox3_STI" alt="chartBox3_STI" style="width: 100%; border: 1px solid #ccc;" />
      </template>
      
      <Chart 
        v-else-if="flat2_STI.length && isChart2Loaded"
        :key="chartKey + '_3sti'"
        :useUrl="useUrl"
        :presetInstrumentIds="flat2_STI"
        :presetDateRange="[past_7days_start, end]"
        :autoRun="true"
        :autoGroup="false"
        :interactive="false"
        :autoVelocity1Hr="true"
        @ready="payload => handleChartReady('chartBox3_STI', payload)"
      />
    </el-card>
  </div>


  </div>
</template>

<script>
import PptxGenJS from 'pptxgenjs';
import html2canvas from 'html2canvas';
import RankTable from '@/components/rank/RankTable.vue';
import Map4Autoreport from '@/components/map/Map4Autoreport.vue';
import Chart from '@/components/chart/index.vue';
import { THSR_PROJECT_ID } from '@/lib/base/project';

export default {
  name: 'typhoon',

  components: {
    RankTable,
    Map4Autoreport,
    Chart,
  },
  props: {
    useUrl: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      alertData: null,
      selectedDate: '',
      datePickerOptions: {
        disabledDate(date) {
          return date > new Date();
        },
      },
      loadingText: '生成報告中，請在此頁面等待...',
      start: '',
      end: '',
      past_7days_start: '',
      rain_time: '',
      isChart1Loaded: false,
      isChart2Loaded: false,
      isDataLoaded: false,
      loading: false,
      projectData: null,
      instrumentList: [],
      manual_content: {
        typhoon_name: '',
        report_number: '第?報',
        sub_title: '(YYYY/MM/DD HH時，EOC?級)',
        report_date: 'YYYY/MM/DD',
      },
      images: [
        'https://api-geoserver.geosupply.com.tw/rain/高鐵邊坡監測/f0_d12s.gif',
        'https://api-geoserver.geosupply.com.tw/rain/高鐵邊坡監測/f1_d12s.gif',
        'https://api-geoserver.geosupply.com.tw/rain/高鐵邊坡監測/f2_d12s.gif',
        'https://api-geoserver.geosupply.com.tw/rain/高鐵邊坡監測/f3_d12s.gif',
      ],
      rain_images: [
        'https://api-geoserver.geosupply.com.tw/rain/高鐵邊坡監測/',
      ],
      pptConfig: {
        // 4:3 layout
        slideWidth: 10,
        slideHeight: 7.5,
        margin: 0.05,
        scale: 0.75,
      },
      flat1: [],
      flat2: [],
      flat1_TM: [],
      flat1_STI: [],
      flat2_TM: [],
      flat2_STI: [],
      chartKey: 0,
      chartKey2: 0,
      chartReadyCount: 0,
      chartImages: {
        chartBox1_TM: null,
        chartBox1_STI: null,
        chartBox2_TM: null,
        chartBox2_STI: null,
        chartBox3_TM: null,
        chartBox3_STI: null,
      },
      chartReplaced: {
        chartBox1_TM: false,
        chartBox1_STI: false,
        chartBox2_TM: false,
        chartBox2_STI: false,
        chartBox3_TM: false,
        chartBox3_STI: false,
      },
    };
  },
  async mounted() {
    this.fetchCapFromAPI();

    const endDate = new Date();
    endDate.setMinutes(0, 0, 0);

    const startDate = new Date(endDate.getTime() - 24 * 60 * 60 * 1000);

    this.selectedDate = [startDate, endDate];
    this.rain_time = endDate;
    this.updateRainTime();
    this.getDate();

    const now = new Date();

    now.setMinutes(0, 0, 0);

    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const hh = String(now.getHours()).padStart(2, '0');

    this.manual_content.report_date = `${yyyy}/${mm}/${dd}`;
    this.manual_content.sub_title = `(${yyyy}/${mm}/${dd} ${hh}時，EOC?級)`;
  },
  methods: {
    formatYMDH(d) {
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}_${pad(d.getHours())}`;
    },
    // 取最近一個 <= rain_time 的 08:00 或 20:00
    roundToCwaBase(dt) {
      const d = new Date(dt);
      d.setMinutes(0, 0, 0);
      const h = d.getHours();
      if (h < 8) {           // 00~07 → 前一天 20:00
        d.setDate(d.getDate() - 1);
        d.setHours(20);
      } else if (h < 20) {   // 08~19 → 當天 08:00
        d.setHours(8);
      } else {               // 20~23 → 當天 20:00
        d.setHours(20);
      }
      return d;
    },
    async updateRainTime() {
      const d = new Date(this.rain_time);

      const yyyy = d.getFullYear();
      const mm   = String(d.getMonth() + 1).padStart(2, '0');
      const dd   = String(d.getDate()).padStart(2, '0');
      const hh   = String(d.getHours()).padStart(2, '0');

      const url = `https://api-geoserver.geosupply.com.tw/rain/高鐵邊坡監測/${yyyy}${mm}${dd}${hh}_chart2.png`;

      // 檢查圖片是否存在
      const exists = await fetch(url, { method: 'HEAD' })
        .then(res => res.ok)
        .catch(() => false);

      if (exists) {
        this.rain_images[0] = url;
      } else {
        console.warn('日累積雨量圖片不存在 → 設為 null');
        this.rain_images[0] = null;
      }
    },
    getDate() {
      try {
        this.start = new Date(this.selectedDate[0]);
        this.end = new Date(this.selectedDate[1]);
        this.past_7days_start = new Date(this.end.getTime() - 7 * 24 * 60 * 60 * 1000);
        const diff = (this.end - this.start) / (1000 * 60 * 60 * 24);

        if (diff > 7) {
          this.$notifyInfo('選擇範圍不能超過七天');
          this.selectedDate = '';
          return;
        }
      } catch (error) {
        this.$notifyInfo('請選擇日期');
      }
    },
    async handleChartReady(chartKey, payload) {
      const { getBase64, chartInstance, yMin, yMax } = payload || {};
      // console.log(`[${chartKey}] ready, yMin=${yMin}, yMax=${yMax}`);

      if (chartInstance) {
        // 等待完全渲染
        await new Promise(resolve => {
          let count = 0;
          const timer = setInterval(() => {
            const hasSeries = chartInstance.series?.length > 0;
            const allVisible = hasSeries && chartInstance.series.every(s => s.data?.length > 0);
            if (chartInstance.hasRendered && hasSeries && allVisible) {
              clearInterval(timer);
              resolve();
            } else if (count++ > 20) {
              clearInterval(timer);
              resolve();
            }
          }, 150);
        });
      }

      await new Promise(r => setTimeout(r, 500));

      // 取得圖片
      if (typeof getBase64 === 'function') {
        try {
          const imgData = await getBase64();
          this.$set(this.chartImages, chartKey, imgData);
          this.$set(this.chartReplaced, chartKey, true); //
          // console.log(`${chartKey} 圖片就緒並已替換`);
        } catch (err) {
          console.error(`${chartKey} 擷取圖片失敗`, err);
        }
      }

      this.loadingText = '載入歷時曲線...';
      // 更新 chart 狀態
      this.chartReadyCount++;
      if (this.chartReadyCount >= 2) this.isChart1Loaded = true;
      if (this.chartReadyCount >= 4) this.isChart2Loaded = true;
    },
    async confirmExport() {
      if (!this.start || !this.end || !this.rain_time) {
        this.$notifyInfo('請選擇時間範圍');
        return;
      }

      this.$confirm('確定要生成報告嗎？(執行時間較長，請勿關閉頁面)', '提示', {
        confirmButtonText: '確定',
        cancelButtonText: '取消',
        type: 'warning',
      }).then(async () => {
        this.loading = true;
        this.loadingText = '取得數據中...';
        await this.getProject();

        const waitUntilLoaded = () => {
          return new Promise(resolve => {
            const interval = setInterval(() => {
              if (this.chartReadyCount === 6) {
                clearInterval(interval);
                resolve();
              }
            }, 1000);
          });
        };

        await waitUntilLoaded();
        this.exportToPpt();
      }).catch(() => {
        console.log('cancel');
      });
    },
    handleTopInstruments(ids, source) {
      // console.log(`handleTopInstruments: ${source}`, ids);

      if (source === 'rank1') {
        this.flat1_TM = ids.tm || [];
        this.flat1_STI = ids.sti || [];
        this.flat1 = [...this.flat1_TM, ...this.flat1_STI];
      } else if (source === 'rank2') {
        this.flat2_TM = ids.tm || [];
        this.flat2_STI = ids.sti || [];
        this.flat2 = [...this.flat2_TM, ...this.flat2_STI];
      }

      if (this.flat1 && this.flat1.length) this.chartKey++;
      if (this.flat2 && this.flat2.length) this.chartKey2++;

      if (this.flat1 && this.flat1.length) this.chartKey++;
      if (this.flat2 && this.flat2.length) this.chartKey2++;
    },
    async getBase64FromUrl(url) {
      try {
        const response = await fetch(url);
        if (!response.ok) {
          console.warn(`Image fetch failed: ${url} → HTTP ${response.status}`);
          return null; // ❗ 不要 throw，避免整個流程卡住
        }

        const blob = await response.blob();
        return await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = (err) => {
            console.warn('FileReader error', err);
            resolve(null); // ❗ 失敗也要 resolve，不能 reject
          };
          reader.readAsDataURL(blob);
        });

      } catch (err) {
        console.warn('getBase64FromUrl() failed:', err);
        return null;
      }
    },
    async getBase64WithFallback(url) {
      try {
        return await this.getBase64FromUrl(url);
      } catch (e) {
        console.warn('Direct fetch failed, trying CORS proxies...', e);
        const candidates = [];
        try {
          const u = new URL(url);
          const weserv = `https://images.weserv.nl/?url=${u.hostname}${u.pathname}${u.search || ''}`;
          candidates.push(weserv);
        // eslint-disable-next-line no-empty
        } catch (_) {}
        candidates.push(`https://cors.isomorphic-git.org/${url}`);

        let lastErr = e;
        for (const p of candidates) {
          try {
            return await this.getBase64FromUrl(p);
          } catch (err) {
            console.warn('Fallback failed:', p, err);
            lastErr = err;
          }
        }
        throw lastErr;
      }
    },
    calculateDimensions(originalWidth, originalHeight, maxWidth, maxHeight) {
      const ratio = originalWidth / originalHeight;
      if (maxWidth / ratio <= maxHeight) {
        return { width: maxWidth, height: maxWidth / ratio };
      } else {
        return { width: maxHeight * ratio, height: maxHeight };
      }
    },
    async addHighchartsSlides_onePerSlide(pptx, containerRef, baseTitle) {
      // 直接從已生成的 base64 圖片取資料
      const imgData = this.chartImages[containerRef];

      if (!imgData) {
        console.warn(`${containerRef} 沒有圖片（可能還沒生成）`);
        return;
      }

      const slide = pptx.addSlide({ masterName: 'defaultMaster' });
      slide.addText(`${baseTitle}`, { placeholder: 'defaultTitle' });
      slide.addImage({ data: imgData, x: 0.9, y: 1.5, w: 8, h: 4.5 });

      const textBoxWidth = this.pptConfig.slideWidth * 0.8;
      const bx = (this.pptConfig.slideWidth - textBoxWidth) / 2 + 0.5;
      const by = this.pptConfig.slideHeight * (6.5 / 7.5);

      const labelMap = {
        chartBox1_TM: '排名前十地滑計歷時曲線',
        chartBox1_STI: '排名前十邊坡滑動監測桿歷時曲線',
        chartBox2_TM: '排名前十地滑計歸零歷時曲線',
        chartBox2_STI: '排名前十邊坡滑動監測桿歸零歷時曲線',
        chartBox3_TM: '排名前十地滑計1小時速率歷時曲線',
        chartBox3_STI: '排名前十邊坡滑動監測桿1小時速率歷時曲線',
      };

      const label = labelMap[containerRef] || '';
      if (label) {
        slide.addText(label, {
          x: bx,
          y: by,
          fontSize: 18,
          fontFace: 'Microsoft JhengHei',
          align: 'center',
        });
      }
    },
    /** 產生圖片區塊投影片 */
    async addImageContainerSlide(slide, item) {
      const { slideWidth, slideHeight, margin, scale } = this.pptConfig;
      const totalImages = this.images.length;
      const availableWidth = slideWidth - 2 * margin;
      const captionH = 0.5;
      const availableHeight = slideHeight - 2 * margin - 1;
      const maxImageWidth = (availableWidth - (totalImages - 1) * margin) / totalImages;
      const base = this.roundToCwaBase(this.rain_time);

      for (let i = 0; i < totalImages; i++) {
        const url = this.images[i];
        try {
          const base64Img = await this.getBase64FromUrl(url);
          const tempImg = new Image();
          tempImg.src = base64Img;
          await new Promise(resolve => { tempImg.onload = resolve; });
          const { width: imgDisplayWidth, height: imgDisplayHeight } = this.calculateDimensions(
            tempImg.naturalWidth,
            tempImg.naturalHeight,
            maxImageWidth,
            availableHeight,
          );
          const xPos = margin + i * scale * (maxImageWidth + margin) + (maxImageWidth - imgDisplayWidth) / 2;
          const yPos = (slideHeight - (imgDisplayHeight + captionH)) / 2;

          slide.addImage({
            data: base64Img,
            x: xPos + item.xPos_offset,
            y: yPos + item.yPos_offset,
            w: imgDisplayWidth * scale,
            h: imgDisplayHeight * scale,
          });
          
          const now = this.roundToCwaBase(new Date());
          const startT = new Date(now.getTime() + i * 12 * 60 * 60 * 1000);
          const endT   = new Date(startT.getTime() + 12 * 60 * 60 * 1000);

          const startStr = this.formatYMDH(startT);
          const endStr   = this.formatYMDH(endT);

          // 兩行置中顯示
          slide.addText(
            [
              { text: startStr, options: { breakLine: true } },
              { text: endStr },
            ],
            {
              x: (xPos + item.xPos_offset),
              y: (yPos + item.yPos_offset) + imgDisplayHeight * scale + 0.05,
              w:  imgDisplayWidth * scale,
              h:  captionH,
              fontFace: 'Microsoft JhengHei',
              fontSize: 16,
              color: '666666',
              align: 'center',
              valign: 'top',
            },
          );
        } catch (err) {
          console.error(`loaded failed.: ${url}`, err);
        }
      }
    },
    /** 產生地圖區塊投影片 */
    async addMapSlide(slide, item) {
      const { slideWidth, slideHeight, margin, scale } = this.pptConfig;
      const mapImgData = await item.slideDiv.captureScreenshot();
      if (!mapImgData) {
        console.warn('mapImgData null.');
        return;
      }
      const tempImg = new Image();
      tempImg.src = mapImgData;
      await new Promise(resolve => { tempImg.onload = resolve; });
      const { width: imgDisplayWidth, height: imgDisplayHeight } = this.calculateDimensions(
        tempImg.naturalWidth,
        tempImg.naturalHeight,
        slideWidth - 2 * margin,
        slideHeight - 2 * margin - 1,
      );
      const xPos = (slideWidth - imgDisplayWidth) / 2;
      const yPos = (slideHeight - imgDisplayHeight) / 2;
      slide.addImage({
        data: mapImgData,
        x: xPos + item.xPos_offset,
        y: yPos + item.yPos_offset,
        w: imgDisplayWidth * scale,
        h: imgDisplayHeight * scale,
      });

      if (!this.rain_images[0]) {
        console.warn('rain image missing.');
      } else {
        // 日累積雨量圖
        const url = this.rain_images[0];

        const base64Img = await this.getBase64FromUrl(url);
        const tempRainImg = new Image();
        tempRainImg.src = base64Img;
        await new Promise(resolve => { tempRainImg.onload = resolve; });

        const availableWidth = slideWidth / 2 - margin * 2; // 左側一半的區域
        const availableHeight = slideHeight - 2 * margin;

        const { width: rainImgDisplayWidth, height: rainImgDisplayHeight } = this.calculateDimensions(
          tempRainImg.naturalWidth,
          tempRainImg.naturalHeight,
          availableWidth,
          availableHeight,
        );

        const xPosRain = margin + item.xPos_offset - 2;
        const yPosRain = (slideHeight - rainImgDisplayHeight * scale) / 2 + item.yPos_offset + 0.5;

        slide.addImage({
          data: base64Img,
          x: xPosRain,
          y: yPosRain,
          w: rainImgDisplayWidth * scale,
          h: rainImgDisplayHeight * scale,
        }); 
      }
    },
    /** 產生一般 HTML 區塊（利用 html2canvas）投影片 */
    async addHtmlSlide(slide, item) {
      const { slideWidth, slideHeight, margin, scale } = this.pptConfig;
      const canvas = await html2canvas(item.slideDiv);
      const imgData = canvas.toDataURL('image/png');
      const { width: imgDisplayWidth, height: imgDisplayHeight } = this.calculateDimensions(
        canvas.width,
        canvas.height,
        slideWidth - 2 * margin,
        slideHeight - 2 * margin - 1,
      );
      const xPos = (slideWidth - imgDisplayWidth) / 2;
      let yPos = (slideHeight - imgDisplayHeight);

      if (item.slideDiv === this.$refs.rankTable1 || item.slideDiv === this.$refs.rankTable2) {
        yPos -= 1.5;
      }

      slide.addImage({
        data: imgData,
        x: xPos + item.xPos_offset,
        y: yPos + item.yPos_offset,
        w: imgDisplayWidth * scale,
        h: imgDisplayHeight * scale,
      });
    },
    /** 產生 PPT 檔案 */
    async exportToPpt() {
      this.loadingText = '開始生成投影片...';

      const { slideWidth, slideHeight } = this.pptConfig;
      const pptx = new PptxGenJS();

      pptx.defineLayout({ name: 'custom', width: slideWidth, height: slideHeight });
      pptx.layout = 'custom';

      // 定義封面與一般投影片母板
      pptx.defineSlideMaster({
        title: 'coverMaster',
        background: { path: 'https://api-geoserver.geosupply.com.tw/rain/高鐵邊坡監測/ptbg2.png' },
        objects: [
          {
            placeholder: {
              options: {
                name: 'coverTitle',
                type: 'title',
                x: 0.5,
                y: 2,
                w: slideWidth - 1,
                h: 1,
                fontSize: 28,
                fontFace: 'Microsoft JhengHei',
                color: 'FF3000',
                bold: false,
              },
            },
          },
          {
            text: {
              text: 'Page',
              options: {
                x: slideWidth - 1.9,
                y: slideHeight - 0.7,
                w: 1.0,
                h: 0.3,
                fontSize: 12,
                fontFace: 'Microsoft JhengHei',
                color: '666666',
                align: 'right',
              },
            },
          },
        ],
        slideNumber: { x: slideWidth - 1.0, y: slideHeight - 0.7, color: '666666', fontSize: 12, fontFace: 'Microsoft JhengHei' },
      });

      pptx.defineSlideMaster({
        title: 'defaultMaster',
        background: { path: 'https://api-geoserver.geosupply.com.tw/rain/高鐵邊坡監測/ptbg.png' },
        objects: [
          {
            placeholder: { options: {
              name: 'defaultTitle', 
              type: 'title',
              x: 0.4, 
              y: 0.564, 
              w: 8, 
              h: 1,
              fontSize: 32, 
              fontFace: 'Microsoft JhengHei', 
              color: 'FF3000', 
              bold: false, 
              align: 'left',
            } },
          },
          {
            text: {
              text: 'Page',
              options: {
                x: slideWidth - 1.9,
                y: slideHeight - 0.7,
                w: 1.0,
                h: 0.3,
                fontSize: 12,
                fontFace: 'Microsoft JhengHei',
                color: '666666',
                align: 'right',
              },
            },
          },
        ],
        slideNumber: { x: slideWidth - 1.0, y: slideHeight - 0.7, color: '666666', fontSize: 12, fontFace: 'Microsoft JhengHei' },
      });

      // 1. 封面幻燈片
      const coverSlide = pptx.addSlide({ masterName: 'coverMaster' });

      const coverLines = [
        { text: `${this.manual_content.typhoon_name}颱風警報`, options: { fontFace: 'Microsoft JhengHei', color: 'FF3000', fontSize: 36, bold: false, breakLine: true } },
        { text: `邊坡自動化監測系統報告 (${this.manual_content.report_number})`, options: { fontFace: 'Microsoft JhengHei', color: 'FF3000', fontSize: 36, bold: false, breakLine: true } },
        { text: this.manual_content.sub_title, options: { fontFace: 'Microsoft JhengHei', color: '0000FF', fontSize: 32, bold: false, breakLine: true } },
        { text: 'SRD/FED', options: { fontFace: 'Microsoft JhengHei', color: '000000', fontSize: 28, bold: true, breakLine: true } },
        { text: this.manual_content.report_date, options: { fontFace: 'Microsoft JhengHei', color: '000000', bold: true, fontSize: 28 } },
      ];

      // 2. 傳陣列給 addText，第二個參數只保留共用設定（含 placeholder）
      coverSlide.addText(
        coverLines,
        { placeholder: 'coverTitle'},
      );

      const tocSlide = pptx.addSlide({ masterName: 'defaultMaster' });
      tocSlide.addText('報告大綱', { placeholder: 'defaultTitle' });

      const tocLines = [
        { text: '一．', options: { color: 'FF6A00' } },
        { text: ' CWA 颱風警報資訊', options: { breakLine: true } },
        { text: '二．', options: { color: 'FF6A00' } },
        { text: ' CWA 定量降雨預報', options: { breakLine: true } },
        { text: '三．', options: { color: 'FF6A00' } },
        { text: ' 台灣高鐵–降雨觀測', options: { breakLine: true } },
        { text: '四．', options: { color: 'FF6A00' } },
        { text: ' 台灣高鐵–邊坡自動化監測數據說明', options: { breakLine: true } },
        { text: '五．', options: { color: 'FF6A00' } },
        { text: ' 初步結論', options: { breakLine: true } },
      ];
      tocSlide.addText(tocLines, {
        x: 0.5, y: 0.5, w: 10, h: 4,
        fontSize: 28, fontFace: 'Microsoft JhengHei',
      });

      // 2. 其他區塊幻燈片
      const slideElements = [
        { slideDiv: this.$refs.alertBox, title: '一、CWA 颱風警報資訊', xPos_offset: 1.5, yPos_offset: -1 },
        { slideDiv: this.$refs.imageContainer, title: '二、CWA 定量降雨預報', xPos_offset: 1.5, yPos_offset: 1 },
        { slideDiv: this.$refs.mapComponent, title: '三、台灣高鐵–降雨觀測', xPos_offset: 3, yPos_offset: 1 },
        { slideDiv: this.$refs.rankTable1, title: '四、台灣高鐵–邊坡自動化監測數據說明', xPos_offset: 1.5, yPos_offset: 1 },
        { slideDiv: this.$refs.rankTable2, title: '四、台灣高鐵–邊坡自動化監測數據說明', xPos_offset: 1.5, yPos_offset: 1 },
      ];

      for (const item of slideElements) {
        if (!item.slideDiv) continue;
        const slide = pptx.addSlide({ masterName: 'defaultMaster' });
        slide.addText(item.title, { placeholder: 'defaultTitle' });

        if (item.slideDiv === this.$refs.alertBox) {
          await this.addCwaAlertSlide(slide);
        } else if (item.slideDiv === this.$refs.imageContainer) {
          await this.addImageContainerSlide(slide, item);

          const imageContainer_content = [
            { text: '1.', options: { color: 'FF6A00', fontSize: 24 } },
            { text: ' 12小時預報圖：', options: { breakLine: true, fontSize: 24 } },
          ];
          slide.addText(imageContainer_content, {
            x: 0.5, y: 0, w: 10, h: 4,
            fontSize: 12, fontFace: 'Microsoft JhengHei',
          });

        } else if (item.slideDiv === this.$refs.mapComponent) {
          await this.addMapSlide(slide, item);
          const bottomY = slideHeight * (6.5 / 7.5);
          slide.addText('1. 降雨分布情形', { x: slideWidth * (1.5 / 10) - 1, y: 1.5, fontSize: 24, fontFace: 'Microsoft JhengHei' });
          slide.addText('台灣24小時累積雨量分布', { x: slideWidth * (7.5 / 10) - 1.6, y: bottomY, fontSize: 18, fontFace: 'Microsoft JhengHei' });
          slide.addText('高鐵沿線邊坡24小時累積雨量分布', { x: slideWidth * (1.5 / 10) - 0.6, y: bottomY, fontSize: 18, fontFace: 'Microsoft JhengHei' });
        } else {
          await this.addHtmlSlide(slide, item);
          const bx = slideWidth * (5 / 13.33);
          const by = slideHeight * (6.5 / 7.5) - 0.5;
          if (item.slideDiv === this.$refs.rankTable1)
            slide.addText('儀器數據排名-指定時間累積變位', { x: bx, y: by, fontSize: 18, fontFace: 'Microsoft JhengHei' });
          else if (item.slideDiv === this.$refs.rankTable2)
            slide.addText('儀器數據排名-變位速率(mm/hr)', { x: bx, y: by, fontSize: 18, fontFace: 'Microsoft JhengHei' });
        }
      }

      await this.addHighchartsSlides_onePerSlide(pptx, 'chartBox1_TM', '四、台灣高鐵–邊坡自動化監測數據說明');
      await this.addHighchartsSlides_onePerSlide(pptx, 'chartBox1_STI', '四、台灣高鐵–邊坡自動化監測數據說明');
      await this.addHighchartsSlides_onePerSlide(pptx, 'chartBox2_TM', '四、台灣高鐵–邊坡自動化監測數據說明');
      await this.addHighchartsSlides_onePerSlide(pptx, 'chartBox2_STI', '四、台灣高鐵–邊坡自動化監測數據說明');
      await this.addHighchartsSlides_onePerSlide(pptx, 'chartBox3_TM', '四、台灣高鐵–邊坡自動化監測數據說明');
      await this.addHighchartsSlides_onePerSlide(pptx, 'chartBox3_STI', '四、台灣高鐵–邊坡自動化監測數據說明');

      
      const conclusion = pptx.addSlide({ masterName: 'defaultMaster' });
      conclusion.addText('五、初步結論', { placeholder: 'defaultTitle' });

      const now = new Date();
      const pad = (n) => n.toString().padStart(2, '0');

      const yymmddhhmmss = 
        pad(now.getFullYear() % 100) + 
        pad(now.getMonth() + 1) + 
        pad(now.getDate()) + 
        pad(now.getHours()) + 
        pad(now.getMinutes()) + 
        pad(now.getSeconds());

      await pptx.writeFile({ fileName: `${yymmddhhmmss}_${this.manual_content.typhoon_name}颱風${this.manual_content.report_number}.pptx` });
      this.loading = false;
      location.reload();
    },
    async getProject() {
      await this.$axios({
        category: 'project',
        method: 'get',
        apiName: `${THSR_PROJECT_ID}/area-station-instrument`,
      })
        .then(res => {
          this.projectData = res.data;
          res.data.area.forEach(area => {
            area.station.forEach(station => {
              station.instrument.forEach(item => {
                this.instrumentList.push(item);
              });
            });
          });
        })
        .catch(e => console.error(e))
        .finally(() => {
          this.isDataLoaded = true;
          this.loadingText = '載入數據排名表...';
        });
    },
    async addCwaAlertSlide(slide) {
      // 若沒資料就略過
      if (!this.alertData) {
        console.warn('alertData is empty.');
        return;
      }

      const { slideWidth, slideHeight, margin, scale } = this.pptConfig;

      const primaryLines = [];
      if (Array.isArray(this.alertData.sections) && this.alertData.sections.length) {
        primaryLines.push({
          text: `1. ${this.alertData.str1}`,
          options: { fontFace: 'Microsoft JhengHei', fontSize: 24, color: '333333', breakLine: true },
        });
        primaryLines.push({
          text: `    1) ${this.alertData.str2}`,
          options: { fontFace: 'Microsoft JhengHei', fontSize: 20, color: '333333', breakLine: true },
        });
        primaryLines.push({
          text: `    2) ${this.alertData.str3}`,
          options: { fontFace: 'Microsoft JhengHei', fontSize: 20, color: '333333', breakLine: true },
        });

      }

      // 文字框位置與大小（保留下方空間放圖片）
      const textboxX = 0.5;
      const textboxY = 1.5;
      const textboxW = slideWidth - 1.0;  // 左右各留 0.5
      const textboxH = 4.0;               // 讓下方保留空間擺 B20.png

      slide.addText(primaryLines, {
        x: textboxX, y: textboxY, w: textboxW, h: textboxH,
        fontFace: 'Microsoft JhengHei',
        align: 'left',
        valign: 'top',
      });

      // --- 圖片（B20.png）置於文字下方 ---
      try {
        const url = 'https://api-geoserver.geosupply.com.tw/rain/高鐵邊坡監測/B20.png';
        const base64Img = await this.getBase64WithFallback(url);

        // 計算圖片擺放大小（等比縮放）
        // 讓圖片寬度大約佔版面 80%（你可依版面微調）
        const maxW = slideWidth * 1;
        const maxH = slideHeight * 0.4;//- (textboxY + textboxH) - 0.6; // 文字框底下到頁底預留 0.6 高度

        // 先載入圖片量尺寸
        const imgProbe = new Image();
        imgProbe.src = base64Img;
        await new Promise(res => { imgProbe.onload = res; });

        const { width: dispW, height: dispH } = this.calculateDimensions(
          imgProbe.naturalWidth,
          imgProbe.naturalHeight,
          maxW,
          maxH,
        );

        // 水平置中，放在文字框底下
        const imgX = (slideWidth - dispW) / 2;
        const imgY = textboxY + textboxH - 2;

        slide.addImage({
          data: base64Img,
          x: imgX,
          y: imgY,
          w: dispW,
          h: dispH,
        });
      } catch (err) {
        console.error('Failed to load B20.png', err);
      }
    },
    async fetchCapFromAPI() {
      try {
        const response = await fetch(
          'https://opendata.cwa.gov.tw/fileapi/v1/opendataapi/W-C0034-001?Authorization=CWB-F9D03D48-7B12-4428-9F33-CDF5E9D1B162&downloadType=WEB&format=CAP',
        );
        const text = await response.text();
        this.parseXML(text);
      } catch (error) {
        console.error('read CAP failed.', error);
      }
    },
    parseXML(xmlString) {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlString, 'application/xml');

      // 小工具
      const get1 = (parent, local) => parent?.getElementsByTagNameNS('*', local)?.[0] || null;
      const getText = (el, def = 'unknown') => (el?.textContent || '').trim() || def;

      const info = get1(xmlDoc, 'info');
      const rawSent = getText(get1(xmlDoc, 'sent'));
      let sent = 'unknown';
      if (rawSent && rawSent !== 'unknown') {
        const d = new Date(rawSent);
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        const hh = String(d.getHours()).padStart(2, '0');
        const min = String(d.getMinutes()).padStart(2, '0');
        sent = `${mm}/${dd} ${hh}:${min}`;
      }
      const headline = getText(get1(info, 'headline'));

      // description / typhoon-info
      const description = get1(info, 'description');

      // 找 section(title="颱風編號" / "警報報數" / "颱風資訊")
      const sections = Array.from(xmlDoc.getElementsByTagNameNS('*', 'section') || []);

      // 找 title="颱風動態" 的那一節
      const dynamicSec = sections.find(sec => sec.getAttribute('title') === '颱風動態');
      const secByTitle = (t) => sections.find(s => s.getAttribute('title') === t) || null;

      const noTitle = (el, def='unknown') => getText(el, def);

      const secNum = noTitle(secByTitle('颱風編號'));
      const warnCount = noTitle(secByTitle('警報報數'));
      const infoSec = secByTitle('颱風資訊');

      // 颱風名稱們 & 強度(scale zh-TW)
      const cwaTyName = getText(get1(infoSec, 'cwa_typhoon_name'));
      const intlTyName = getText(get1(infoSec, 'typhoon_name'));
      const analysis = get1(infoSec, 'analysis');
      const scales = Array.from(analysis?.getElementsByTagNameNS('*', 'scale') || []);
      const scaleZh = getText(scales.find(s => s.getAttribute('lang') === 'zh-TW'));

      // 若你仍需要 this.alertData 供別處使用（保留你原本的結構）
      const topSections = Array.from(description?.children || [])
        .filter(el => el.localName === 'section')
        .map(el => ({
          title: el.getAttribute('title') || 'unknown',
          content: (el.textContent || '').trim(),
        }));

      // ===== 你要的兩個字串 =====
      // 1) {scale lang="zh-TW"}{cwa_typhoon_name}({typhoon_name}){headline}
      const str1 = `${scaleZh} ${cwaTyName} (國際命名${intlTyName}) ${headline}`;

      // 2) 編號第{section title="颱風編號"}號颱風警報第{section title="警報報數"}報(發布時間:{sent})
      const str2 = `編號第${secNum}號颱風警報第${warnCount}報 (發布時間：${sent})。`;
      const str3 = dynamicSec.textContent;
      
      this.alertData = {
        str1,
        str2,
        str3,
        sections: topSections,
      };
    },
  },
};
</script>

<style>
.box-card {
  margin-bottom: 10px;
  max-width: 1455px;
}

.container {
  margin: auto;
  text-align: center;
  font-family: Arial, sans-serif;
}

.alert-box {
  border: 2px solid red;
  padding: 15px;
  background: #ffecec;
  margin-top: 20px;
}

button {
  margin: 10px;
  padding: 10px;
  background-color: #007bff;
  color: white;
  border: none;
  cursor: pointer;
}

.image-container {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 20px;
}

.image-container img {
  width: 24%;
  min-width: 150px;
  height: auto;
  border: 1px solid #ccc;
  border-radius: 5px;
}
</style>
